/**
 * Natalon Mini App - Main Application
 */

import { useState, useCallback, useEffect } from 'react';
import { BirthDataForm, ChartSelector, ChartWheel, DestinyMatrixView, FamilyManager, InterpretationView, Settings, TopicsList } from './components';
import { useTelegramApp } from './hooks';
import { api } from './api';
import type { BirthData, NatalChart, PlanetPosition, Aspect, ChartSaveRequest, TopicInfo } from './types';
import './styles/global.css';
import styles from './App.module.css';

type AppView = 'selector' | 'form' | 'chart' | 'interpretation' | 'settings' | 'matrix' | 'family';

function App() {
  // Auth is handled by TelegramAuthProvider - api.setInitData() is called before this component renders
  const {
    isReady,
    showBackButton,
    hideBackButton,
    showMainButton,
    hideMainButton,
    haptic,
    setMainButtonLoading,
    openBotWithChart,
  } = useTelegramApp();
  const [view, setView] = useState<AppView>('selector');
  const [chart, setChart] = useState<NatalChart | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [warning, setWarning] = useState<string | null>(null);
  const [selectedPlanet, setSelectedPlanet] = useState<PlanetPosition | null>(null);
  const [selectedAspect, setSelectedAspect] = useState<Aspect | null>(null);
  const [savedChartId, setSavedChartId] = useState<string | null>(null);
  const [returnToFamily, setReturnToFamily] = useState(false);
  const [interpretationTopic, setInterpretationTopic] = useState<TopicInfo | null>(null);

  // Handle chart selection from saved charts
  const handleSelectChart = useCallback(async (chartId: string) => {
    setLoading(true);
    setError(null);

    try {
      const response = await api.getChartById(chartId);

      if (response.success && response.chart) {
        setChart(response.chart);
        setSavedChartId(chartId); // Chart is already saved
        setView('chart');
        showBackButton(() => {
          setView('selector');
          setSavedChartId(null);
          hideBackButton();
        });
      } else {
        setError(response.error || 'Failed to load chart');
        haptic.notification('error');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Connection error');
      haptic.notification('error');
    } finally {
      setLoading(false);
    }
  }, [showBackButton, hideBackButton, haptic]);

  // Navigate to create new chart form
  const handleCreateNew = useCallback(() => {
    setView('form');
    showBackButton(() => {
      setView('selector');
      hideBackButton();
    });
  }, [showBackButton, hideBackButton]);

  // Navigate to settings
  const handleOpenSettings = useCallback(() => {
    setView('settings');
  }, []);

  // Navigate to family
  const handleOpenFamily = useCallback(() => {
    setView('family');
    showBackButton(() => {
      setView('selector');
      hideBackButton();
    });
  }, [showBackButton, hideBackButton]);

  // Navigate to create chart from family flow
  const handleCreateChartForFamily = useCallback(() => {
    setReturnToFamily(true);
    setView('form');
    showBackButton(() => {
      setReturnToFamily(false);
      setView('family');
      hideBackButton();
    });
  }, [showBackButton, hideBackButton]);

  // Navigate back from settings
  const handleBackFromSettings = useCallback(() => {
    setView('selector');
    hideBackButton();
  }, [hideBackButton]);

  // Parse UTC offset string ("+03:00") to minutes
  const parseUtcOffsetToMinutes = (offset: string): number => {
    const match = offset.match(/^([+-])(\d{2}):(\d{2})$/);
    if (!match) return 0;
    const [, sign, hours, minutes] = match;
    const totalMinutes = parseInt(hours, 10) * 60 + parseInt(minutes, 10);
    return sign === '-' ? -totalMinutes : totalMinutes;
  };

  // Handle form submission
  const handleFormSubmit = useCallback(async (birthData: BirthData) => {
    setLoading(true);
    setError(null);
    setMainButtonLoading(true);

    try {
      const response = await api.calculateChart({
        birthDate: birthData.date,
        birthTime: birthData.time,
        latitude: birthData.location.latitude,
        longitude: birthData.location.longitude,
      });

      if (response.success && response.chart) {
        setChart(response.chart);

        // Save chart to database
        try {
          const saveRequest: ChartSaveRequest = {
            name: birthData.name,
            birthDate: birthData.date,
            birthTime: birthData.time,
            birthTimeKnown: birthData.timeKnown,
            latitude: birthData.location.latitude,
            longitude: birthData.location.longitude,
            locationName: birthData.location.displayNameShort,
            houseSystem: 'placidus',
            planets: response.chart.planets,
            houses: response.chart.houses,
            aspects: response.chart.aspects,
            points: response.chart.points,
            elementBalance: response.chart.elementBalance,
            modeBalance: response.chart.modeBalance,
            dignities: response.chart.dignities || {},
            timezone: response.chart.timezone || '',
            utcOffset: parseUtcOffsetToMinutes(response.chart.utcOffset || '+00:00'),
          };

          const saveResponse = await api.saveChart(saveRequest);
          if (saveResponse.success && saveResponse.chartId) {
            setSavedChartId(saveResponse.chartId);
          }
        } catch (saveErr) {
          // Show warning to user but don't block the flow
          console.error('Failed to save chart:', saveErr);
          setWarning('Карта не сохранена в базу данных');
          // Auto-dismiss warning after 5 seconds
          setTimeout(() => setWarning(null), 5000);
        }

        if (returnToFamily) {
          setReturnToFamily(false);
          setView('family');
          showBackButton(() => {
            setView('selector');
            hideBackButton();
          });
        } else {
          setView('chart');
          showBackButton(() => {
            setView('selector');
            setSavedChartId(null);
            hideBackButton();
          });
        }
      } else {
        setError(response.error || 'Ошибка расчёта карты');
        haptic.notification('error');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Ошибка соединения');
      haptic.notification('error');
    } finally {
      setLoading(false);
      setMainButtonLoading(false);
    }
  }, [showBackButton, hideBackButton, haptic, setMainButtonLoading, returnToFamily]);

  // Handle planet click
  const handlePlanetClick = useCallback((planet: PlanetPosition) => {
    setSelectedPlanet(planet);
    setSelectedAspect(null);
  }, []);

  // Handle aspect click
  const handleAspectClick = useCallback((aspect: Aspect) => {
    setSelectedAspect(aspect);
    setSelectedPlanet(null);
  }, []);

  // Close info panel
  const closeInfoPanel = useCallback(() => {
    setSelectedPlanet(null);
    setSelectedAspect(null);
    haptic.impact('light');
  }, [haptic]);

  // Manage MainButton based on current view
  useEffect(() => {
    if (view === 'selector') {
      showMainButton('Создать карту', handleCreateNew);
    } else if (view === 'chart' && savedChartId) {
      showMainButton('Открыть в боте', () => {
        openBotWithChart(savedChartId);
      });
    } else if (view !== 'form') {
      // Form manages its own MainButton
      hideMainButton();
    }

    return () => {
      hideMainButton();
    };
  }, [view, savedChartId, showMainButton, hideMainButton, openBotWithChart, handleCreateNew]);

  // Loading state - wait for SDK features (UI, haptics, etc.)
  if (!isReady) {
    return (
      <div className={styles.loadingContainer}>
        <div className={styles.loadingSpinner} />
        <p>Загрузка...</p>
      </div>
    );
  }

  return (
    <div className={styles.app}>
      {view === 'selector' && (
        <ChartSelector
          onSelectChart={handleSelectChart}
          onOpenSettings={handleOpenSettings}
          onOpenFamily={handleOpenFamily}
        />
      )}

      {view === 'settings' && (
        <Settings onBack={handleBackFromSettings} />
      )}

      {view === 'family' && (
        <FamilyManager onBack={() => { setView('selector'); hideBackButton(); }} onCreateChart={handleCreateChartForFamily} />
      )}

      {view === 'form' && (
        <BirthDataForm onSubmit={handleFormSubmit} />
      )}

      {view === 'chart' && chart && (
        <div className={styles.chartView}>
          <ChartWheel
            chart={chart}
            onPlanetClick={handlePlanetClick}
            onAspectClick={handleAspectClick}
          />

          {/* Planet Info Panel */}
          {selectedPlanet && (
            <div className={styles.infoPanel}>
              <button className={styles.closeBtn} onClick={closeInfoPanel}>✕</button>
              <h3>{selectedPlanet.nameRu}</h3>
              <div className={styles.infoGrid}>
                <div className={styles.infoItem}>
                  <span className={styles.label}>Знак</span>
                  <span className={styles.value}>{selectedPlanet.signRu}</span>
                </div>
                <div className={styles.infoItem}>
                  <span className={styles.label}>Градус</span>
                  <span className={styles.value}>{selectedPlanet.degree.toFixed(1)}°</span>
                </div>
                <div className={styles.infoItem}>
                  <span className={styles.label}>Дом</span>
                  <span className={styles.value}>{selectedPlanet.house}</span>
                </div>
                {selectedPlanet.retrograde && (
                  <div className={styles.infoItem}>
                    <span className={styles.label}>Ретроградность</span>
                    <span className="text-destructive">{selectedPlanet.retrograde ? 'Ретро' : ''}</span>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Aspect Info Panel */}
          {selectedAspect && (
            <div className={styles.infoPanel}>
              <button className={styles.closeBtn} onClick={closeInfoPanel}>✕</button>
              <h3>{selectedAspect.typeRu}</h3>
              <div className={styles.infoGrid}>
                <div className={styles.infoItem}>
                  <span className={styles.label}>Планеты</span>
                  <span className={styles.value}>
                    {chart.planets[selectedAspect.planet1]?.nameRu} — {chart.planets[selectedAspect.planet2]?.nameRu}
                  </span>
                </div>
                <div className={styles.infoItem}>
                  <span className={styles.label}>Угол</span>
                  <span className={styles.value}>{selectedAspect.angle.toFixed(1)}°</span>
                </div>
                <div className={styles.infoItem}>
                  <span className={styles.label}>Орб</span>
                  <span className={styles.value}>{selectedAspect.orb.toFixed(1)}°</span>
                </div>
                <div className={styles.infoItem}>
                  <span className={styles.label}>Тип</span>
                  <span className={styles.value}>
                    {selectedAspect.applying ? 'Сходящийся' : 'Расходящийся'}
                  </span>
                </div>
              </div>
            </div>
          )}

          {/* Destiny Matrix Button */}
          {chart.destinyMatrix && (
            <button
              className={styles.matrixButton}
              onClick={() => {
                setView('matrix');
                showBackButton(() => {
                  setView('chart');
                  showBackButton(() => {
                    setView('selector');
                    setSavedChartId(null);
                    hideBackButton();
                  });
                });
                haptic.impact('light');
              }}
            >
              🔮 Матрица Судьбы
            </button>
          )}

          {/* Element Balance */}
          <div className={styles.balanceSection}>
            <h4>Баланс стихий</h4>
            <div className={styles.balanceBar}>
              <div
                className={`${styles.balanceSegment} ${styles.fire}`}
                style={{ flex: chart.elementBalance.fire }}
                title={`Огонь: ${chart.elementBalance.fire}`}
              />
              <div
                className={`${styles.balanceSegment} ${styles.earth}`}
                style={{ flex: chart.elementBalance.earth }}
                title={`Земля: ${chart.elementBalance.earth}`}
              />
              <div
                className={`${styles.balanceSegment} ${styles.air}`}
                style={{ flex: chart.elementBalance.air }}
                title={`Воздух: ${chart.elementBalance.air}`}
              />
              <div
                className={`${styles.balanceSegment} ${styles.water}`}
                style={{ flex: chart.elementBalance.water }}
                title={`Вода: ${chart.elementBalance.water}`}
              />
            </div>
            <div className={styles.balanceLabels}>
              <span>🔥 {chart.elementBalance.fire}</span>
              <span>🌍 {chart.elementBalance.earth}</span>
              <span>💨 {chart.elementBalance.air}</span>
              <span>💧 {chart.elementBalance.water}</span>
            </div>
          </div>

          {/* Interpretation Topics */}
          {savedChartId && (
            <TopicsList
              onSelectTopic={(topic) => {
                setInterpretationTopic(topic);
                setView('interpretation');
                showBackButton(() => {
                  setView('chart');
                  setInterpretationTopic(null);
                  showBackButton(() => {
                    setView('selector');
                    setSavedChartId(null);
                    hideBackButton();
                  });
                });
                haptic.impact('light');
              }}
            />
          )}
        </div>
      )}

      {view === 'interpretation' && savedChartId && interpretationTopic && (
        <InterpretationView
          chartId={savedChartId}
          topic={interpretationTopic}
          onBack={() => {
            setView('chart');
            setInterpretationTopic(null);
            showBackButton(() => {
              setView('selector');
              setSavedChartId(null);
              hideBackButton();
            });
          }}
        />
      )}

      {view === 'matrix' && chart?.destinyMatrix && (
        <DestinyMatrixView
          matrix={chart.destinyMatrix}
          onClose={() => {
            setView('chart');
            showBackButton(() => {
              setView('selector');
              setSavedChartId(null);
              hideBackButton();
            });
          }}
          onInterpret={savedChartId ? () => {
            setInterpretationTopic({
              key: 'destiny_matrix',
              name: 'Матрица Судьбы',
              description: 'Расшифровка матрицы судьбы',
              icon: '🔮',
              isPaid: false,
              order: 0,
            });
            setView('interpretation');
            showBackButton(() => {
              setView('matrix');
              setInterpretationTopic(null);
              showBackButton(() => {
                setView('chart');
                showBackButton(() => {
                  setView('selector');
                  setSavedChartId(null);
                  hideBackButton();
                });
              });
            });
            haptic.impact('light');
          } : undefined}
        />
      )}

      {/* Loading Overlay */}
      {loading && (
        <div className={styles.loadingOverlay}>
          <div className={styles.loadingSpinner} />
          <p>Загрузка...</p>
        </div>
      )}

      {/* Error Toast */}
      {error && (
        <div className={`${styles.errorToast} fade-in`} onClick={() => setError(null)}>
          <span>❌ {error}</span>
        </div>
      )}

      {/* Warning Toast */}
      {warning && (
        <div className={`${styles.warningToast} fade-in`} onClick={() => setWarning(null)}>
          <span>⚠️ {warning}</span>
        </div>
      )}
    </div>
  );
}

export default App;
